import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { GitCompare, Plus } from 'lucide-react';
import { Button } from '@artstorefronts/ui';
import AdminLayout from '../../../components/AdminLayout';
import { ACQUISITION_STRATEGIES, type Strategy } from './queries';
import { ActivatePanel } from './ActivatePanel';
import { GATE_LABELS, METRIC_ROWS, StatusBadge, formatDate } from './shared';

const TH = 'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 whitespace-nowrap';
const TD = 'px-4 py-3 text-sm whitespace-nowrap';

function weightCell(strategy: Strategy): string {
  if (strategy.status !== 'active') {
    return '—';
  }
  return `${strategy.trafficWeight}%`;
}

function StrategiesTable({ strategies }: { strategies: Strategy[] }) {
  return (
    <div className="overflow-x-auto rounded-lg bg-white shadow">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className={TH}>Version</th>
            <th className={TH}>Name</th>
            <th className={TH}>Status</th>
            <th className={TH}>Weight</th>
            <th className={TH}>Activated</th>
            <th className={TH}>Deactivated</th>
            {METRIC_ROWS.map((row) => (
              <th key={row.label} className={`${TH} text-right`} title={row.hint}>
                {row.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {strategies.map((s) => (
            <tr key={s.id} className="hover:bg-gray-50">
              <td className={`${TD} font-medium text-gray-900`}>
                <Link
                  to={`/admin/acquisition/${s.id}`}
                  className="inline-flex min-h-11 items-center underline-offset-2 hover:underline"
                >
                  v{s.version}
                </Link>
              </td>
              <td className={`${TD} text-gray-900`}>
                <Link to={`/admin/acquisition/${s.id}`} className="hover:underline">
                  {s.name}
                </Link>
                <div className="text-xs text-gray-500">{GATE_LABELS[s.settings.signupGate.mode]}</div>
              </td>
              <td className={TD}>
                <StatusBadge status={s.status} />
              </td>
              <td className={`${TD} text-gray-700`}>{weightCell(s)}</td>
              <td className={`${TD} text-gray-500`}>{formatDate(s.activatedAt)}</td>
              <td className={`${TD} text-gray-500`}>{formatDate(s.deactivatedAt)}</td>
              {METRIC_ROWS.map((row) => (
                <td key={row.label} className={`${TD} text-right tabular-nums text-gray-700`}>
                  {row.value(s.metrics)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AcquisitionStrategiesPage() {
  const { data, loading, error, refetch } = useQuery<{ acquisitionStrategies: Strategy[] }>(ACQUISITION_STRATEGIES, {
    fetchPolicy: 'cache-and-network',
  });
  const strategies = data?.acquisitionStrategies ?? [];

  const actions = (
    <div className="flex flex-wrap gap-2">
      <Button variant="outline" size="sm" className="h-11 gap-1.5 md:h-9" asChild>
        <Link to="/admin/acquisition/compare">
          <GitCompare className="h-3.5 w-3.5 shrink-0" aria-hidden />
          Compare
        </Link>
      </Button>
      <Button variant="default" size="sm" className="h-11 gap-1.5 bg-gray-900 hover:bg-gray-800 md:h-9" asChild>
        <Link to="/admin/acquisition/new">
          <Plus className="h-3.5 w-3.5 shrink-0" aria-hidden />
          New strategy
        </Link>
      </Button>
    </div>
  );

  return (
    <AdminLayout
      title="Acquisition strategies"
      description="Versioned bundles of signup settings. One runs at a time, or two at a weight split for an A/B test. Signed-up members keep their strategy, and preview links keep showing theirs (a signup through a preview link gets an active strategy). Visitors who haven't signed up are re-assigned from the active strategies if theirs is archived. A strategy's settings freeze once it has run."
      action={actions}
    >
      <div className="space-y-6 px-4 md:px-0">
        {error && <p className="text-sm text-red-600">{error.message}</p>}
        {loading && strategies.length === 0 ? (
          <div className="text-gray-500">Loading strategies…</div>
        ) : (
          <StrategiesTable strategies={strategies} />
        )}
        {strategies.length > 0 && <ActivatePanel strategies={strategies} onActivated={() => void refetch()} />}
      </div>
    </AdminLayout>
  );
}

export default AcquisitionStrategiesPage;
